import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const NoticeContainer = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  padding: 1rem 2rem;
  text-align: center;
  box-shadow: 0 2px 10px rgba(238, 90, 36, 0.3);
  position: relative;
  z-index: 50;
`;

const NoticeContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const NoticeText = styled.div`
  flex: 1;

  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.9rem;
  }
`;

const NoticeActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ResendButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  color: #ee5a24;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    transform: translateY(-1px);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const EmailVerificationNotice: React.FC = () => {
  const { currentUser, sendVerificationEmail, isEmailVerified } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Don't show if user is not logged in or email is already verified
  if (!currentUser || isEmailVerified) {
    return null;
  }

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      await sendVerificationEmail();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000); // Hide success message after 5 seconds
    } catch (error: any) {
      setResendError(error.message || 'Failed to send verification email');
      setTimeout(() => setResendError(''), 5000);
    } finally {
      setIsResending(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <NoticeContainer>
      <NoticeContent>
        <NoticeText>
          <h4>⚠️ Email Verification Required</h4>
          <p>
            Please verify your email address ({currentUser.email}) to access all features.
            Check your inbox and click the verification link.
          </p>
          {resendSuccess && (
            <SuccessMessage>
              ✅ Verification email sent! Check your inbox.
            </SuccessMessage>
          )}
          {resendError && (
            <SuccessMessage>
              ❌ {resendError}
            </SuccessMessage>
          )}
        </NoticeText>
        <NoticeActions>
          <ResendButton
            onClick={handleResendVerification}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Resend Email'}
          </ResendButton>
          <RefreshButton onClick={handleRefresh}>
            I Verified My Email
          </RefreshButton>
        </NoticeActions>
      </NoticeContent>
    </NoticeContainer>
  );
};

export default EmailVerificationNotice;